import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/api";
import { useAuth } from "@/auth";
import { Send } from "lucide-react";
import { fmtTime, dateSeparatorLabel, isNewDay } from "@/lib/time";

const MAX_LEN = 2000;

function DateSeparator({ label }) {
  return (
    <div className="flex items-center justify-center my-3" data-testid="date-separator">
      <span className="text-[11px] font-medium text-[#968F85] bg-white rounded-full px-3 py-1 border border-[#EDECEA]">{label}</span>
    </div>
  );
}

// Reusable conversation thread — same data source as the Viestit page (GET /messages/{other}, POST /messages).
export function ConversationPanel({ otherUserId, otherName = "Käyttäjä", campaignId, canInitiate = false }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    if (!otherUserId) return;
    try {
      const { data } = await api.get(`/messages/${otherUserId}`);
      setMessages(data);
      window.dispatchEvent(new Event("unread-refresh"));
    } catch { /* ignore */ }
    finally { setLoaded(true); }
  }, [otherUserId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages]);

  const canSend = canInitiate || messages.length > 0;

  const send = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || sending || !otherUserId) return;
    setSending(true);
    try {
      const payload = { receiver_id: otherUserId, text: value };
      if (campaignId) payload.campaign_id = campaignId;
      await api.post("/messages", payload);
      setText("");
      await load();
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(err.response?.data?.detail || "Viestin lähetys epäonnistui");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="conversation-panel" className="rounded-2xl border border-[#E6E1D9] overflow-hidden flex flex-col bg-white">
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-3 bg-[#F6F8FB] min-h-[220px] max-h-[420px]">
        {!loaded ? (
          <p className="text-center text-sm text-[#968F85] mt-8">Ladataan keskustelua…</p>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-[#968F85] mt-8" data-testid="conversation-empty">Ei viestejä vielä.</p>
        ) : messages.map((m, i) => {
          const mine = m.sender_id === user.user_id;
          const showSep = i === 0 || isNewDay(messages[i - 1].created_at, m.created_at);
          return (
            <div key={m.message_id}>
              {showSep && <DateSeparator label={dateSeparatorLabel(m.created_at)} />}
              <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid="conversation-message">
                <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words ${mine ? "bg-[#292827] text-white" : "bg-white border border-[#EDECEA] text-[#292827]"}`}>
                  {m.text}
                </div>
                <span className="mt-1 text-[11px] text-[#968F85] px-1">{mine ? "Sinä" : otherName} · {fmtTime(m.created_at)}</span>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {canSend ? (
        <form onSubmit={send} className="flex gap-2 p-3 border-t border-[#E6E1D9] bg-white">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Kirjoita viesti..." maxLength={MAX_LEN}
            data-testid="conversation-input"
            className="flex-1 rounded-full bg-white border border-[#E6E1D9] px-5 py-3 outline-none focus:border-[#292827] transition-colors" />
          <button type="submit" disabled={sending || !text.trim()} data-testid="conversation-send"
            className="h-12 px-5 rounded-full bg-[#292827] text-white flex items-center gap-2 hover:bg-black transition-colors disabled:opacity-40">
            <Send size={17} strokeWidth={1.75} /> <span className="hidden sm:inline text-sm font-medium">Lähetä</span>
          </button>
        </form>
      ) : (
        <div className="p-4 border-t border-[#E6E1D9] bg-white text-sm text-[#968F85]" data-testid="conversation-locked">
          Yritys ei ole vielä aloittanut keskustelua. Voit vastata heti, kun yritys ottaa sinuun yhteyttä.
        </div>
      )}
    </div>
  );
}
